/** Orders, commands and reconciliation: the broker lifecycle apart from the command lifecycle (U041-U045). */
import {
  decodeBalanceRow,
  decodeOrderRow,
  decodeReconciliationRow,
  type BalanceRow,
  type OrderRow,
  type ReconciliationRow,
} from "../../contracts/account.ts";
import { decodeCommandRow, type CommandRow } from "../../contracts/commands.ts";
import { badge, definitions, instantCell, renderFreshnessBar } from "../../app/components.ts";
import { openInspector } from "../../app/command-dialog-view.ts";
import { h, setChildren, type Child } from "../../app/dom.ts";
import { formatPaise } from "../../app/format.ts";
import { hashOf } from "../../app/router.ts";
import { renderStateBanner, stateOfEnvelope, stateOfError } from "../../app/state.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { CommandSurface } from "../command-surface.ts";
import { handleOf, makeFrame } from "../frame.ts";
import {
  bookQuantity,
  buildTree,
  isCancellable,
  openDifferences,
  quantityText,
  unresolvedExposure,
  type OrderNode,
} from "./model.ts";

const COMMANDS_SHOWN = 50;
const UNRESOLVED_STATES = new Set(["APPLYING", "UNKNOWN"]);

function stateTone(state: string): "ok" | "warn" | "bad" | "info" {
  if (state === "FILLED") {
    return "ok";
  }
  if (state === "UNKNOWN" || state === "REJECTED") {
    return "bad";
  }
  return state === "PARTIALLY_FILLED" || state === "CANCEL_PENDING" ? "warn" : "info";
}

function orderItem(node: OrderNode, surface: CommandSurface, account: BalanceRow): HTMLElement {
  const { row } = node;
  const cancel =
    surface.offered("cancel-order") && isCancellable(row)
      ? surface.launcher({
          intent: "cancel-order",
          scope: `order ${row.orderId} (${row.instrument})`,
          quantities: quantityText(row),
          payload: { order_id: row.orderId },
        })
      : null;
  return h(
    "li",
    { attrs: { "data-order": row.orderId, "data-state": row.state } },
    h(
      "div",
      { class: "order" },
      h("strong", {}, row.orderId),
      ` ${row.instrument} ${row.side} ${row.role.replace("_", " ").toLowerCase()} `,
      badge(row.state, stateTone(row.state)),
      ` ${quantityText(row)}`,
      row.limitPaise === null ? ", market" : `, limit ${formatPaise(row.limitPaise)}`,
      "; updated ",
      instantCell(row.updatedAt),
      node.orphan ? " (its parent order is not in this page)" : "",
      row.state === "UNKNOWN"
        ? h("strong", { class: "flag" }, " UNKNOWN: not filled, not closed.")
        : null,
      " ",
      cancel,
    ),
    node.children.length === 0
      ? null
      : h("ul", {}, ...node.children.map((child) => orderItem(child, surface, account))),
  );
}

function commandList(rows: readonly CommandRow[], ctx: ViewContext): HTMLElement {
  const shown = [...rows]
    .sort((a, b) => b.receivedAt.localeCompare(a.receivedAt))
    .slice(0, COMMANDS_SHOWN);
  return h(
    "section",
    { attrs: { "aria-labelledby": "orders-commands" } },
    h("h2", { attrs: { id: "orders-commands" } }, "Command lifecycle"),
    h(
      "p",
      { class: "note" },
      "A command's own states (requested, accepted, applying, applied, failed, unknown) are separate from its orders' broker states above. An unknown command stays here until it is inspected and reconciled.",
    ),
    shown.length === 0
      ? h("p", {}, "No commands for this account.")
      : h(
          "ul",
          { class: "command-rows" },
          ...shown.map((row) => {
            const inspect = h(
              "button",
              {
                attrs: {
                  type: "button",
                  "data-inspect": row.commandId,
                  "aria-label": `Inspect command ${row.commandId.slice(0, 8)}: ${row.action} on account ${row.accountId}`,
                },
                on: {
                  click: () => {
                    const dialog = openInspector(
                      { services: ctx.commands },
                      row.commandId,
                      inspect,
                    );
                    inspect.ownerDocument.body.append(dialog.element);
                  },
                },
              },
              "Inspect",
            );
            return h(
              "li",
              { attrs: { "data-command": row.commandId, "data-state": row.state } },
              badge(row.state, UNRESOLVED_STATES.has(row.state) ? "bad" : "info"),
              ` ${row.action} by ${row.actorIdentityId}; command ${row.commandId.slice(0, 8)}; expects `,
              [...row.targetVersions]
                .map(([name, version]) => `${name} v${String(version)}`)
                .join(", "),
              "; received ",
              instantCell(row.receivedAt),
              " ",
              inspect,
            );
          }),
        ),
  );
}

function differenceList(rows: readonly ReconciliationRow[]): HTMLElement {
  const open = openDifferences(rows);
  return h(
    "section",
    { attrs: { "aria-labelledby": "orders-reconciliation" } },
    h("h2", { attrs: { id: "orders-reconciliation" } }, "Internal versus broker"),
    h(
      "p",
      { class: "note" },
      `${String(open.length)} of ${String(rows.length)} differences are not resolved. A correction is a new event with a trace; historic fills are never edited.`,
    ),
    rows.length === 0
      ? h("p", {}, "No differences between the internal books and the broker.")
      : h(
          "div",
          {
            class: "table-wrap",
            attrs: { tabindex: "0", role: "region", "aria-label": "Reconciliation differences" },
          },
          h(
            "table",
            { class: "data-table" },
            h("caption", {}, "Reconciliation differences"),
            h(
              "thead",
              {},
              h(
                "tr",
                {},
                ...[
                  "Difference",
                  "Kind",
                  "Instrument",
                  "Order",
                  "Internal",
                  "Broker",
                  "Status",
                  "Detected",
                  "Trace",
                ].map((header) => h("th", { attrs: { scope: "col" } }, header)),
              ),
            ),
            h(
              "tbody",
              {},
              ...rows.map((row) => {
                const cells: Child[] = [
                  row.differenceId,
                  row.kind.replaceAll("_", " ").toLowerCase(),
                  row.instrument,
                  row.orderId ?? "none",
                  bookQuantity(row.internalQuantity),
                  bookQuantity(row.brokerQuantity),
                  badge(row.resolution, row.resolution === "RESOLVED" ? "ok" : "bad"),
                  instantCell(row.detectedAt),
                  h("span", {}, row.snapshotRef, ": ", row.explanation),
                ];
                return h(
                  "tr",
                  {
                    attrs: {
                      "data-difference": row.differenceId,
                      "data-resolution": row.resolution,
                    },
                  },
                  ...cells.map((cell) => h("td", {}, cell)),
                );
              }),
            ),
          ),
        ),
  );
}

/** Mount the orders view; without broker routing it shows the blocker and nothing else (ruling 0.14). */
export function mountOrders(ctx: ViewContext): ViewHandle {
  const frame = makeFrame("orders", "Orders, commands and reconciliation");
  if (!ctx.capabilities.routingEnabled) {
    frame.banner.append(
      renderStateBanner({
        kind: "unavailable",
        reason:
          "orders and reconciliation are not enabled before G5 (broker routing is off; the server's capability flag is false). The command activity drawer still lists every command",
      }),
    );
    return handleOf(frame, ctx);
  }
  if (ctx.accountId === null) {
    frame.banner.append(renderStateBanner({ kind: "no-observations" }));
    return handleOf(frame, ctx);
  }
  frame.banner.append(renderStateBanner({ kind: "loading" }));
  const surface = new CommandSurface(ctx, load);

  async function load(): Promise<void> {
    surface.reset();
    const params = { accountId: ctx.accountId };
    try {
      const [orders, differences, balances, commands] = await Promise.all([
        ctx.client.read("orders", decodeOrderRow, params),
        ctx.client.read("reconciliation", decodeReconciliationRow, params),
        ctx.client.read("balances", decodeBalanceRow, params),
        ctx.client.read("commands", decodeCommandRow, params),
      ]);
      if (
        orders === null ||
        differences === null ||
        balances === null ||
        commands === null ||
        ctx.scope.disposed
      ) {
        return;
      }
      const state = stateOfEnvelope(orders);
      setChildren(frame.banner, state.kind === "ready" ? [] : [renderStateBanner(state)]);
      setChildren(frame.freshness, [
        renderFreshnessBar(orders, { streamSequence: ctx.streamSequence.get() }),
      ]);
      const [account] = balances.page.rows;
      const rows: readonly OrderRow[] = orders.page.rows;
      const unresolved = unresolvedExposure(rows);
      surface.clearLaunchers();
      setChildren(frame.body, [
        surface.status,
        h(
          "p",
          {},
          h(
            "a",
            { attrs: { href: hashOf("orders", "routing") } },
            "Routing controls: block entry, modify, cancel, take over, resume, raise a limit",
          ),
        ),
        h(
          "section",
          { attrs: { "aria-labelledby": "orders-unresolved" } },
          h("h2", { attrs: { id: "orders-unresolved" } }, "Unresolved exposure"),
          unresolved.length === 0
            ? h("p", {}, "No unknown, partial or unprotected orders reported.")
            : // The alert role sits on a wrapper so the list keeps its list role (axe `listitem`).
              h(
                "div",
                { class: "unresolved", attrs: { role: "alert" } },
                h(
                  "ul",
                  {},
                  ...unresolved.map((item) =>
                    h(
                      "li",
                      { attrs: { "data-unresolved": item.orderId } },
                      h("strong", {}, `${item.orderId} ${item.instrument}: `),
                      item.reason,
                    ),
                  ),
                ),
              ),
        ),
        h(
          "section",
          { attrs: { "aria-labelledby": "orders-tree" } },
          h("h2", { attrs: { id: "orders-tree" } }, "Orders (broker and execution lifecycle)"),
          rows.length === 0 || account === undefined
            ? h("p", {}, "No orders for this account.")
            : h(
                "ul",
                { class: "order-tree" },
                ...buildTree(rows).map((node) => orderItem(node, surface, account)),
              ),
          account === undefined
            ? null
            : definitions([["Account state version", `v${String(account.accountVersion)}`]]),
        ),
        differenceList(differences.page.rows),
        commandList(commands.page.rows, ctx),
      ]);
      surface.observe("orders", orders);
      surface.observe("balances", balances);
      surface.identifyFrom(balances);
    } catch (error) {
      setChildren(frame.banner, [renderStateBanner(stateOfError(error))]);
    }
  }

  void load();
  return handleOf(frame, ctx);
}
